import { PlanName, PlanNameEnum } from 'base/accounts_service';
import { AnalyticsService } from 'base/analytics';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { Rows } from 'base/ui/layout';
import { TickRow } from 'base/ui/tick_row';
import { TextRegularLight } from 'base/ui/typography';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { ProFeatureGateDialog } from 'blocks/billing/feature_gate_dialog';
import { AccessLevel } from 'blocks/blocks_service';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { SaveThenNavigateButton } from './auth_dialog';

interface SyncStatus {
  syncNow: () => void;
  hasUnsavedChanges: boolean;
}

export type ShowUpgradeWallFn = (
  feature: 'projects' | 'pages' | 'share' | 'export' | 'unlock',
) => void;

export function createUpgradeDialogs(
  {
    accessLevel,
    flags,
  }: { accessLevel: AccessLevel; flags: FeatureFlagsStore },
  disableUnloadConfirmation: () => Promise<void>,
  sync: SyncStatus,
  planName?: PlanNameEnum,
) {
  const dialogBox = observable.box<React.ReactElement | undefined>();
  function close() {
    dialogBox.set(undefined);
  }
  function showUpgradeWall(
    feature: 'projects' | 'pages' | 'share' | 'export' | 'unlock',
    analytics: AnalyticsService,
  ) {
    // Start saving changes, so they'll be saved by the time the presses upgrade
    if (dialogBox.get()) return;
    sync.syncNow();
    dialogBox.set(
      <ProFeatureGateDialog
        analytics={analytics}
        currentPlan={planName ?? PlanName.FREE}
        initialFeatureKey={feature}
        onClose={close}
      />,
    );
  }

  return {
    showUpgradeWall,
    getDialog: () => dialogBox.get(),
    headerButton:
      accessLevel >= AccessLevel.OWNER && flags.get('showUpgradeButton') ? (
        <SaveThenNavigateButton
          variant="primary"
          href={getUpgradeHref()}
          disableUnloadConfirmation={disableUnloadConfirmation}
          label="Upgrade"
          gradient
        />
      ) : undefined,
  };
}

function getUpgradeHref() {
  return authRouteToUrl({
    path: 'upgrade',
    context: { redirect: getCurrentLocationForRedirect() },
  });
}

const UpsellDialog = observer(function _UpsellDialog({
  onClose,
  disableUnloadConfirmation,
}: {
  onClose: () => void;
  disableUnloadConfirmation: () => Promise<void>;
}) {
  return (
    <Dialog
      title="Upgrade your plan"
      onClose={onClose}
      buttons={
        <>
          <Button label="Cancel" onClick={onClose} size="large" />
          <SaveThenNavigateButton
            label="Upgrade"
            variant="primary"
            disableUnloadConfirmation={disableUnloadConfirmation}
            href={getUpgradeHref()}
            size="large"
          />
        </>
      }
    >
      <TextRegularLight>
        You need a paid account to unlock the following features:
      </TextRegularLight>
      <Rows spacing={8}>
        <TickRow>View all wireframe pages</TickRow>
        <TickRow>Export wireframes to Figma and Webflow</TickRow>
        <TickRow>Create more than 1 project</TickRow>
      </Rows>
    </Dialog>
  );
});
